import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { IotConfigResponse, iotConfigType, iotConfigUseTab, IotTabFormData, UnionIotConfigUseTab } from "src/api/iot/iot-config-type";
import { IotDisplayTab } from "src/api/iot/iot-types";
import { ContentsTitle } from "src/components";
import MetaTag from "src/components/layout/MetaTag";
import useNavigate from "src/hooks/usePartnerNavigate";
import { useQueryParams } from "src/hooks/useQueryParams";
import { PagePath } from "src/pages/product/details";
import { globalPartnerState } from "src/recoil/partners/atom";
import { controlDeviceDefaultTabs, iotDisplayTabs, spaceDisplayDefaultTabs } from "../iot-config-constant";
import IotUseTabSetting from "./components/IotUseTabSetting";
import { useIotTab } from "./hooks/useIotTab";

const IotTabManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [iotDisplayConfig, setIotDisplayConfig] = useState<IotConfigResponse>();
  const [iotCustomTabList, setIotCustomTabList] = useState<IotDisplayTab[]>([]);
  const [iotDefaultTabList, setIotDefaultTabList] = useState<IotDisplayTab[]>([]);
  const partner = useRecoilValue(globalPartnerState);
  const hasIotControlFeature = useMemo(() => partner?.features?.find((feature) => feature.name === "FEATURE_IOT_CONTROL")?.active, [partner]);

  const { queryParams } = useQueryParams();

  // API 함수들을 훅에서 가져오기
  const {
    getPartnerIotSettingInfo, //
    saveIotDisplayUseTab,
    getIotDisplayCustomTabList,
    assignIotDeviceToCustomTab,
    deleteCustomTabDevice,
    addIotDisplayCustomTab,
    deleteIotDisplayCustomTab,
    updateIotDisplayCustomTab,
    getIotDeviceToRegister,
  } = useIotTab();

  const activeTab = useMemo(() => {
    if (queryParams?.tab) {
      return iotDisplayTabs.find((tab) => tab.value === queryParams?.tab);
    }
    return iotDisplayTabs[0];
  }, [queryParams]);

  // 탭 클릭시 callback 함수
  const clickTab = useCallback(
    (tab: { value: string; label: string }) => {
      const newQueryParams = { ...queryParams };
      if (newQueryParams?.tab) {
        delete newQueryParams.tab;
      }

      newQueryParams.tab = tab.value;

      // query parameter 에 tab 추가
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [location, queryParams],
  );

  useEffect(() => {
    if (!hasIotControlFeature && activeTab?.value === "display-device-control") {
      navigate(PagePath.notFound);
    }
  }, [hasIotControlFeature]);

  useEffect(() => {
    Promise.all([getIotConfigSetting(), getIotCustomTabList()]);
  }, [activeTab]);

  // IoT 탭 설정 > 기본 탭 / 커스텀 탭 토글 변경
  const onChangeIotDisplayUseTab = async (useTabs: UnionIotConfigUseTab[]) => {
    await saveIotDisplayUseTab({
      type: activeTab?.value!,
      useTabs: useTabs,
    });
    await getIotConfigSetting();
  };

  // 커스텀 탭에 기기연결하기
  const onUpdateAssignDevices = async (registeredDeviceIds: string[], groupId: string, type: "assign" | "delete" = "assign") => {
    if (type === "assign") {
      await assignIotDeviceToCustomTab({
        groupId,
        registeredDeviceIds,
      });
      await getIotCustomTabList();
    } else if (type === "delete") {
      await deleteCustomTabDevice({
        groupId,
        registeredDeviceId: registeredDeviceIds[0],
      });
      await getIotCustomTabList();
    }
  };

  // 공간정보 / 기기제어 탭 조회 (IoT 탭 페이지 진입시 조회)
  const getIotConfigSetting = async () => {
    const { data } = await getPartnerIotSettingInfo({ configType: activeTab?.value! });
    setIotDisplayConfig(data.data.config);
  };

  // 탭 관리 영역 > 커스텀 탭 목록 조회
  const getIotCustomTabList = async () => {
    const { data } = await getIotDisplayCustomTabList({ configType: activeTab?.value! });
    setIotCustomTabList([...data.data.content.map((item) => ({ ...item, type: iotConfigUseTab.CUSTOM_TAB }))]);
  };

  // 탭 관리 영역 > 커스텀 탭 추가
  const onAddIotCustomTab = async (tab: IotTabFormData) => {
    if (!tab.originalId) {
      await addIotDisplayCustomTab({
        name: tab.name || "",
        priority: iotCustomTabList.length > 0 ? Math.max(...iotCustomTabList.map((item) => item.priority || 0)) + 1 : 1,
        configType: activeTab?.value!,
      });
      await getIotCustomTabList();
    } else {
      await updateIotDisplayCustomTab({ groupId: tab.originalId!, name: tab.name || "", priority: tab.priority! });
      await getIotCustomTabList();
    }
  };

  // 탭 관리 영역 > 커스텀 탭 삭제
  const onDeleteCustomTab = async (groupId: string) => {
    await deleteIotDisplayCustomTab({ groupId });
    await getIotCustomTabList();
  };

  // iotDisplayTabList가 변경될 때 form 값 동기화
  useEffect(() => {
    const fetchAndSetValue = async () => {
      const { data } = await getIotDeviceToRegister();
      const devices = data.data.content;
      const defaultTabList = activeTab?.value === iotConfigType.DISPLAY_SPACE ? spaceDisplayDefaultTabs : controlDeviceDefaultTabs;

      const updatedTabList = defaultTabList.map((tab) => {
        if (tab.type === iotConfigUseTab.DEFAULT_TAB) {
          // 해당 탭에 매칭되는 모든 디바이스 찾기
          const matchingDevices =
            devices?.filter((device) => device.labels?.some((label) => label.name === "용도" && tab.labelValue?.includes(label.value))) || [];

          return {
            ...tab,
            registeredDevices: matchingDevices,
          };
        }
        return tab;
      });
      setIotDefaultTabList(updatedTabList);
    };
    fetchAndSetValue();
  }, [activeTab]);

  const iotDisplayTabList = useMemo(() => {
    return [...iotCustomTabList, ...iotDefaultTabList];
  }, [iotCustomTabList, iotDefaultTabList]);

  return (
    <div>
      <MetaTag subTitle={"IoT 탭 설정"} />
      <ContentsTitle
        title=""
        tabs={hasIotControlFeature ? iotDisplayTabs : iotDisplayTabs.filter((tab) => tab.value !== iotConfigType.DISPLAY_DEVICE_CONTROL)}
        activeTab={activeTab}
        clickTab={clickTab}
      />
      <div className="contents-container__scroll">
        {/* 기본정보 */}
        {iotDisplayTabList.length > 0 && (
          <IotUseTabSetting
            iotDisplayConfig={iotDisplayConfig}
            iotDisplayTabList={iotDisplayTabList}
            onChangeIotDisplayUseTab={onChangeIotDisplayUseTab}
            onUpdateAssignDevices={onUpdateAssignDevices}
            onAddIotCustomTab={onAddIotCustomTab}
            onDeleteCustomTab={onDeleteCustomTab}
          />
        )}
      </div>
    </div>
  );
};

export default IotTabManager;
